import React, { useContext, useState } from "react";
import styles from "../../styles/cr.module.scss";
import { RagIndicator } from "../cr/RagIndicator";
import UpdateRagDisplay from "./UpdateRagDisplay";
import { RagColours } from "../../refData/RagColours";
import { IBaseComponentProps, IBenefitUpdate, IReportDueDates, IReportingEntity, ISignOff, ListDefaults, ProjectUpdate } from "../../types";
import { DateService } from "../../services";
import { getUpdateReportingStage, UpdateReportingStages } from "../../refData/UpdateReportingStages";
import { report } from "process";
import { Panel, PanelType } from "@fluentui/react/lib/Panel";
import { ProjectProgressUpdateForm } from "../project/ProjectProgressUpdateForm";
import { IconButton } from "@fluentui/react/lib/Button";
import { OrbUserContext } from "../OrbUserContext";
import { WorkStreamProgressUpdateReviewList } from "../workStream/WorkStreamProgressUpdateReviewList";
import { EntityStatus } from "../../refData/EntityStatus";
import { BenefitProgressUpdateReviewList } from "../benefit/BenefitProgressUpdateReviewList";
import { MilestoneProgressUpdateReviewList } from "../milestone/MilestoneProgressUpdateReviewList";
import { ReportTypes } from "../../refData/ReportTypes";
import { DependencyProgressUpdateReviewList } from "../dependency/DependencyProgressUpdateReviewList";

interface IProjectUpdatesAndSignOff extends IBaseComponentProps {
    reportDates: IReportDueDates;
    report: Partial<ISignOff>;
    previousReport: ISignOff;
    previousBenefitUpdates: IBenefitUpdate[];
    loadReport: () => void;
    approvedByText?: string;
    showPreviousUpdate?: boolean;
}

const ProjectUpdatesAndSignOff = (props: IProjectUpdatesAndSignOff) => {

    const { userPermissions } = useContext(OrbUserContext);
    const [showForm, setShowForm] = useState(false);
    const project = props.report?.Project;
    const projectId = project?.ID;
    const latestUpdates = project?.ProjectUpdates?.[0];
    const pu = latestUpdates || new ProjectUpdate();

    // read-only logic
    const readOnly =
        (props.report?.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
        UpdateReportingStages.UpdatesInProgress;

    const hideEdit = !!readOnly;

    // edit button
    const editButton = (
        projectId &&
        !hideEdit &&
        userPermissions.UserCanSubmitProjectUpdates(projectId) && (
            <IconButton
                iconProps={{ iconName: "Edit" }}
                title="Edit"
                onClick={() => setShowForm(true)}
            />
        )
    );



    const renderDetailsSection = () => {

        const project = props.report?.Project;

        const projectTitle = project?.Title;
        const updatedBy = project?.ProjectUpdates?.[0]?.UpdateUser?.Title;
        const approvedBy = props.approvedByText;
        const period = DateService.dateToUkLongDate(props.reportDates?.Next);
        const stage = getUpdateReportingStage(project?.ProjectUpdates?.[0]?.ReportingStageID);
        const objectives = project?.Objectives;

        const parentElement = (() => {
            if (!project) return null;

            // Portfolio can come either directly or via Programme
            const portfolioTitle =
                project.Portfolio?.Title || project.Programme?.Portfolio?.Title || "";

            const portfolio = portfolioTitle ? (
                <span>
                    <i>Portfolio</i> {portfolioTitle}
                </span>
            ) : null;

            const programme = project.Programme ? (
                <span>
                    <i>Programme</i> {project.Programme.Title}
                </span>
            ) : null;

            if (project.ParentTypeID === 1) {
                // Only portfolio
                return <>{portfolio}</>;
            } else if (project.ParentTypeID === 2) {
                // Programme, but show portfolio too if exists
                return (
                    <>
                        {portfolio}
                        {portfolio && programme ? ", " : ""}
                        {programme}
                    </>
                );
            }

            return null;
        })();



        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex" }}>
                        <div className={styles.sectionATitle}>Details</div>
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr>
                                <td className="label">Project</td>
                                <td className="value">{projectTitle}</td>
                                <td className="label">Parent</td>
                                <td className="value">{parentElement}</td>
                            </tr>
                            <tr>
                                <td className="label">Period</td>
                                <td className="value">{period}</td>
                                <td className="label">Status</td>
                                <td className="value">{stage}</td>
                            </tr>
                            <tr>
                                <td className="label">Updated By</td>
                                <td className="value">{updatedBy}</td>
                                <td className="label">Approved By</td>
                                <td className="value">{approvedBy}</td>
                            </tr>
                            <tr>
                                <td style={{ verticalAlign: 'top' }} className="label">Project Objectives</td>
                                <td className="value" colSpan={3}>
                                    {objectives}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    // Render headlines section
    const renderHeadlinesSection = () => {
        const project = props.report?.Project;
        const latestUpdates = project?.ProjectUpdates?.[0];
        const pu = latestUpdates || new ProjectUpdate();

        // Calculations / mappings
        const deliveryConfidenceRag = pu.OverallRagOptionID;
        const deliveryConfidenceDescription = pu.ProgressUpdate || ListDefaults.placeholders.dataTBC;
        const furtherActions = pu.FutureActions || ListDefaults.placeholders.dataTBC;
        const escalations = pu.Escalations || ListDefaults.placeholders.dataTBC;
        const currentPhase = pu?.['ProjectPhase']?.Title ?? "-";
        const wholeLifeCost = formatCurrency(pu?.WholeLifeCost);
        const wholeLifeBenefit = formatCurrency(pu?.WholeLifeBenefit);
        const netPresentValue = formatCurrency(pu?.NetPresentValue);

        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div className={styles.sectionATitle}>Headlines</div>
                        {editButton}
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr className="invisible-row">
                                <td className="label"></td>
                                <td className="value"></td>
                                <td className="label"></td>
                                <td className="value"></td>
                            </tr>

                            <tr className="first-visible-row">
                                <td className="label">Delivery Confidence Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={deliveryConfidenceRag} />
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Delivery Confidence Description</td>
                                <td className="value" colSpan={3}>
                                    {deliveryConfidenceDescription}
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Further Actions</td>
                                <td className="value" colSpan={3}>
                                    {furtherActions}
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Escalations for senior leader action</td>
                                <td className="value" colSpan={3}>
                                    {escalations}
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Current Phase</td>
                                <td className="value">{currentPhase}</td>
                                <td className="label">Whole Life Cost</td>
                                <td className="value">{wholeLifeCost}</td>
                            </tr>

                            <tr>
                                <td className="label">Whole Life Benefit</td>
                                <td className="value">{wholeLifeBenefit}</td>
                                <td className="label">Net Present Value</td>
                                <td className="value">{netPresentValue}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    // Render key indicators section
    const renderKeyIndicatorsSection = () => {
        // const project = props.report?.Project;
        // const latestUpdates = project?.ProjectUpdates?.[0];
        // const pu = latestUpdates || new ProjectUpdate();

        // Calculations / mappings
        const financeRag = pu.FinanceRagOptionID;
        const financeDetails = pu.FinanceComment || ListDefaults.placeholders.dataTBC;

        const milestonesRag = pu.MilestonesRagOptionID;
        const milestonesDetails = pu.MilestonesComment || ListDefaults.placeholders.dataTBC;

        const peopleRag = pu.PeopleRagOptionID;
        const peopleDetails = pu.PeopleComment || ListDefaults.placeholders.dataTBC;

        const benefitsRag = pu.BenefitsRagOptionID;
        const benefitsDetails = pu.BenefitsComment || ListDefaults.placeholders.dataTBC;

        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div className={styles.sectionATitle}>Key Indicators</div>
                        {editButton}
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr>
                                <td className="label">Finance Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={financeRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Finance Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {financeDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">People Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={peopleRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>People Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {peopleDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Milestones Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={milestonesRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Milestones Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {milestonesDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Benefits Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={benefitsRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Benefits Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {benefitsDetails}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };



    const formatCurrency = (value: number | string | null | undefined): string => {
        if (value == null) return "-"; // null or undefined

        if (typeof value === "number") {
            return `£${value.toLocaleString()}`;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed === "") return "-"; // empty string
            const num = Number(trimmed);
            return isNaN(num) ? "-" : `£${num.toLocaleString()}`;
        }

        return "-";
    };

    const closeEditUpdate = (reloadList: boolean): void => {
        setShowForm(false);
        if (reloadList) props.loadReport?.();
    };

    const isOpen = (entity: IReportingEntity, updatesProp: string): boolean => entity.EntityStatusID === EntityStatus.Open || entity[updatesProp]?.length > 0;

    return (
        <div className={styles.cr} style={{ width: '99%', minHeight: '400px', position: 'relative' }}>
            {/* Main Heading */}
            <h1 style={{ fontWeight: '500' }} className={styles.headings2}>
                Project Updates
            </h1>

            {/* Details Section */}
            {renderDetailsSection()}

            {/* Headlines Section */}
            {renderHeadlinesSection()}

            {/* Indicators Section */}
            {renderKeyIndicatorsSection()}

            <Panel isOpen={showForm} headerText="Edit project update" type={PanelType.medium} onDismiss={() => setShowForm(false)}>
                <ProjectProgressUpdateForm
                    {...props}
                    entityId={projectId}
                    entityUpdateId={pu?.ID}
                    defaultShowForm={true}
                    onSaved={() => closeEditUpdate(true)}
                    onCancelled={() => closeEditUpdate(false)}
                />
            </Panel>

            <br /><br />
            <WorkStreamProgressUpdateReviewList
                {...props}
                //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                readOnly={
                    (props.report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) > UpdateReportingStages.UpdatesInProgress
                }

                entities={props.report.WorkStreams?.filter(w => isOpen(w, 'WorkStreamUpdates'))}
                previousEntities={props.previousReport?.WorkStreams}
                showPreviousUpdate={props.showPreviousUpdate}
                onChange={props.loadReport}
            />

            <div style={{ marginTop: '20px' }}>
                <MilestoneProgressUpdateReviewList
                    {...props}
                    //readOnly={report.Project?.ProjectUpdates?.[0]?.ReportingStageID > UpdateReportingStages.UpdatesInProgress ?? false}
                    readOnly={
                        (props.report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
                        UpdateReportingStages.UpdatesInProgress
                    }

                    entities={props.report.Milestones?.filter(m => isOpen(m, 'MilestoneUpdates'))}
                    previousEntities={props.previousReport?.Milestones}
                    showPreviousUpdate={props.showPreviousUpdate}
                    onChange={props.loadReport}
                    reportType={ReportTypes.Project}
                />
            </div>
            <div style={{ marginTop: '20px' }}>
                <BenefitProgressUpdateReviewList
                    {...props}
                    readOnly={
                        (props.report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) > UpdateReportingStages.UpdatesInProgress
                    }

                    entities={props.report.Benefits?.filter(b => isOpen(b, 'BenefitUpdates'))}
                    previousEntities={props.previousReport?.Benefits}
                    previousBenefitUpdates={props.previousBenefitUpdates}
                    showPreviousUpdate={props.showPreviousUpdate}
                    onChange={props.loadReport}
                />
            </div>

            <div style={{ marginTop: '20px' }}>
                <DependencyProgressUpdateReviewList
                    {...props}
                    readOnly={
                        (props.report.Project?.ProjectUpdates?.[0]?.ReportingStageID ?? 0) >
                        UpdateReportingStages.UpdatesInProgress
                    }

                    entities={props.report.Dependencies?.filter(d => isOpen(d, 'DependencyUpdates'))}
                    previousEntities={props.previousReport?.Dependencies}
                    showPreviousUpdate={props.showPreviousUpdate}
                    onChange={props.loadReport}
                />
            </div>

        </div>
    );
};

export default ProjectUpdatesAndSignOff;
